import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dataService from 'services/dataService';
import { API_PODS } from 'services/dataService/resources';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import { ApiError } from '../utils/api-error';
import { classifyPodError } from '../utils/pod-error-classifier';

interface IUseDeletePodOptions {
  onSuccess?: () => void;
}

export const useDeletePod = ({ onSuccess }: IUseDeletePodOptions = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async (guid: string) => {
      const { data, error, status } = await dataService.deleteOne(API_PODS, {
        id: guid,
      });
      if (error) {
        throw new ApiError(error, status);
      }
      return data;
    },
    onSuccess: () => {
      notificationController.success({
        message: t('labels.podDeletedSuccessfully'),
      });

      queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
      queryClient.invalidateQueries({ queryKey: [API_PODS, 'detail'] });

      onSuccess?.();
    },
    onError: (error: unknown) => {
      const classified = classifyPodError(error);

      if (classified.type === 'INSUFFICIENT_PRIVILEGES') {
        notificationController.error({
          message: t('labels.noPermissionToDeletePod'),
        });
        return;
      }

      if (classified.type === 'POD_NOT_FOUND') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
        return;
      }

      // VALIDATION_ERROR (400): surface backend's raw message
      if (classified.type === 'VALIDATION_ERROR') {
        notificationController.error({
          message: classified.fallback,
        });
        return;
      }

      // GENERIC (500+/unknown): use delete-specific message
      notificationController.error({
        message: t('labels.unableToDeletePod'),
      });
    },
  });

  const deletePod = (guid: string, callbacks?: { onSettled?: () => void }) => {
    mutate(guid, { onSettled: callbacks?.onSettled });
  };

  return { deletePod, isLoading };
};
